import { useState } from "react";
import { useQuery } from "@tanstack/react-query";

import {
  getAbbreviationsByShort,
  listTerminologyItems,
} from "@/api/terminology";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";

import { candidateOnlyHint } from "./senseSearchHints";
import type { SenseTarget } from "./senseSearchTypes";

export function SenseSearch({
  allow = ["glossary_term", "abbreviation"],
  placeholder = "Найти термин или аббревиатуру (RU/EN)…",
  onPick,
  pickedItemId,
}: {
  allow?: Array<"glossary_term" | "abbreviation">;
  placeholder?: string;
  onPick: (target: SenseTarget) => void;
  pickedItemId?: string | null;
}) {
  const [raw, setRaw] = useState("");
  const q = useDebouncedValue(raw.trim(), 400);
  // «короткое» (≤8, без пробела) — возможно акроним → дополнительно тянем
  // by-short (все смыслы). Substring гоняем всегда — он ловит и аббревиатуры
  // (DOAC по «doac»), и термины, не завися от регистра/эвристики.
  const shortish = q.length >= 2 && q.length <= 8 && !q.includes(" ");
  const useByShort = shortish && allow.includes("abbreviation");

  const byShort = useQuery({
    queryKey: ["intent-by-short", q],
    queryFn: () => getAbbreviationsByShort(q),
    enabled: useByShort,
  });
  const bySubstr = useQuery({
    queryKey: ["intent-term-search", q, allow.join(",")],
    queryFn: () => listTerminologyItems({ q, status: "approved", limit: 20 }),
    enabled: q.length >= 2,
  });

  const cards: SenseTarget[] = [];
  if (useByShort && byShort.data?.ok) {
    for (const a of byShort.data.data.abbreviations) {
      if (!a.item_id) continue;
      cards.push({
        kind: "abbreviation",
        item_id: a.item_id,
        short: a.source_abbreviation,
        source_text: a.source_abbreviation,
        source_expansion: a.source_expansion,
        target_abbreviation: a.target_abbreviation,
        target_text: a.target_expansion,
        domain: a.domain,
        source_lang: a.source_lang,
        target_lang: a.target_lang,
        status: a.status,
      });
    }
  }
  if (bySubstr.data?.ok) {
    for (const it of bySubstr.data.data.items) {
      if (it.item_type !== "glossary_term" && it.item_type !== "abbreviation")
        continue;
      if (!allow.includes(it.item_type)) continue;
      cards.push({
        kind: it.item_type,
        item_id: it.item_id,
        short: it.item_type === "abbreviation" ? it.source_text : undefined,
        source_text: it.source_text,
        source_expansion: it.source_expansion,
        target_abbreviation: it.target_abbreviation,
        target_text: it.target_text,
        domain: it.domain,
        source_lang: it.source_lang,
        target_lang: it.target_lang,
        status: it.status,
      });
    }
  }

  // Дедуп: одна и та же пара en→ru может прийти из нескольких source_key
  // (eaeu_inn / nsi / okhlp …) — для пользователя это одна карточка.
  const ql = q.toLowerCase();
  const seen = new Set<string>();
  const deduped = cards.filter((c) => {
    const key = [
      c.kind,
      (c.source_text ?? "").toLowerCase(),
      (c.target_text ?? "").toLowerCase(),
      (c.domain ?? "").toLowerCase(),
    ].join("|");
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  // Релевантность: точное совпадение → префикс → подстрока. «омепразол»
  // поднимает omeprazole→омепразол выше esomeprazole→эзомепразол.
  const rel = (c: { source_text?: string | null; target_text?: string | null }) => {
    const s = (c.source_text ?? "").toLowerCase();
    const t = (c.target_text ?? "").toLowerCase();
    if (s === ql || t === ql) return 0;
    if (s.startsWith(ql) || t.startsWith(ql)) return 1;
    return 2;
  };
  deduped.sort((a, b) => rel(a) - rel(b));

  const loading = (useByShort && byShort.isFetching) || bySubstr.isFetching;
  const polysemic =
    useByShort && byShort.data?.ok ? byShort.data.data.is_polysemic : false;
  // Пусто в approved, но есть кандидаты в очереди (ХБП до 2026-09-14): не
  // «ничего не нашлось», а «не одобрено» — говорим прямо и ведём заводить.
  const candHint =
    useByShort && byShort.data?.ok && deduped.length === 0
      ? candidateOnlyHint(byShort.data.data)
      : null;

  return (
    <div>
      <input
        className="mt-input"
        style={{ width: "100%" }}
        placeholder={placeholder}
        value={raw}
        onChange={(e) => setRaw(e.target.value)}
      />
      {q.length >= 1 && (
        <div style={{ marginTop: 8 }}>
          {loading && <span className="mt-muted">Ищу…</span>}
          {!loading && deduped.length === 0 && candHint && (
            <div className="rf-example" style={{ padding: 8, fontSize: "var(--fs-sm)" }}>
              <b>{q}</b> в словаре пока <b>не одобрено</b> — переводчик его не
              видит. В очереди на разбор {candHint.candidateCount}{" "}
              {candHint.candidateCount === 1 ? "кандидат" : "кандидата(ов)"} из
              клинреков
              {candHint.topDefinition ? (
                <>
                  , чаще всего: «{candHint.topDefinition}»
                </>
              ) : null}
              . Заведи запись через «…добавить новый термин/аббревиатуру» — после
              этого сюда можно вернуться.
            </div>
          )}
          {!loading && deduped.length === 0 && !candHint && (
            <span className="mt-muted">
              Ничего не нашлось — попробуй иначе (RU/EN, точное сокращение).
            </span>
          )}
          {(polysemic || deduped.length > 1) && deduped.length > 1 && (
            <div className="mt-muted" style={{ marginBottom: 6 }}>
              Выбери нужный вариант:
            </div>
          )}
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {deduped.map((c) => {
              const picked = c.item_id === pickedItemId;
              return (
                <button
                  key={c.item_id}
                  type="button"
                  className="mt-card mt-card--clickable"
                  style={{
                    textAlign: "left",
                    padding: 8,
                    fontSize: "var(--fs-sm)",
                    border: picked
                      ? "2px solid var(--accent, #2563eb)"
                      : undefined,
                  }}
                  onClick={() => onPick(c)}
                >
                  <b>{c.short ?? c.source_text}</b>
                  {" → "}
                  <b>{c.target_abbreviation ?? c.target_text}</b>
                  {c.source_expansion ? (
                    <span className="mt-muted"> · {c.source_expansion}</span>
                  ) : null}
                  {c.domain ? (
                    <span className="mt-muted"> ({c.domain})</span>
                  ) : null}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
